"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Lock, Mail } from "lucide-react";
import { Turnstile, turnstileEnabled } from "@/components/auth/Turnstile";
import { PautaMark } from "@/components/brand/PautaMark";
import { safeNextPath, signInErrorMessage } from "@/lib/authPolicy";
import { createClient } from "@/lib/supabase/client";

function LoginInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = safeNextPath(searchParams.get("next"));
  const isSlackConnection = next.startsWith("/connect/slack/");
  const expired = searchParams.get("expired") === "1";
  const reset = searchParams.get("reset") === "1";
  const setupComplete = searchParams.get("setup") === "complete";
  const setupRecovered = searchParams.get("setup") === "recovered";
  const linkError = searchParams.get("error") === "invalid_link";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [error, setError] = useState<string | null>(
    linkError ? "That sign-in or recovery link is invalid or expired." : null
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const sb = createClient();
      const { error: signInError } = await sb.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
        options: { captchaToken: captchaToken ?? undefined },
      });
      if (signInError) throw signInError;
      router.replace(next);
      router.refresh();
    } catch (reason) {
      setError(signInErrorMessage(reason));
      setCaptchaToken(null);
      setCaptchaResetKey((current) => current + 1);
    } finally {
      setLoading(false);
    }
  }

  const nextQuery = next === "/" ? "" : `?next=${encodeURIComponent(next)}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--bg)] noise">
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="w-full max-w-sm"
      >
        <div className="mb-6 text-center">
          <PautaMark size={56} className="mx-auto mb-3" />
          <h1 className="text-[20px] font-semibold tracking-tight">Pauta</h1>
          <p className="text-[12.5px] text-[var(--fg-tertiary)] mt-1">
            {isSlackConnection
              ? "Sign in to connect your Slack identity"
              : "Sign in securely with your password"}
          </p>
        </div>

        <div className="rounded-xl border border-[var(--border-strong)] bg-[var(--surface-elevated)] p-6">
          {isSlackConnection && (
            <div className="mb-4 rounded-md border border-[var(--border-strong)] bg-[var(--bg-secondary)] px-3 py-2 text-[12px] leading-relaxed text-[var(--fg-secondary)]">
              Use the Pauta account you want Slack to act as. After sign-in,
              you&apos;ll return to a confirmation screen.
            </div>
          )}
          {expired && (
            <div className="mb-4 rounded-md border border-[var(--border-strong)] bg-[var(--bg-secondary)] px-3 py-2 text-[12px] text-[var(--fg-secondary)]">
              Your session expired. Sign in again to continue.
            </div>
          )}

          {reset && (
            <div className="mb-4 rounded-md border border-[var(--green)]/30 bg-[var(--green)]/10 px-3 py-2 flex items-start gap-2 text-[12px] text-[var(--fg-secondary)]">
              <Check size={13} className="text-[var(--green)] mt-0.5 shrink-0" />
              Password updated. Sign in with your new password.
            </div>
          )}

          {(setupComplete || setupRecovered) && (
            <div className="mb-4 rounded-md border border-[var(--green)]/30 bg-[var(--green)]/10 px-3 py-2 flex items-start gap-2 text-[12px] text-[var(--fg-secondary)]">
              <Check size={13} className="text-[var(--green)] mt-0.5 shrink-0" />
              {setupRecovered
                ? "Recovery finished. Sign in with the password from your first setup attempt, or use Forgot password below."
                : "Account ready. Sign in with the password you just chose."}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-[11px] uppercase tracking-wide text-[var(--fg-muted)] font-medium">
                Email
              </label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)]" />
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-md bg-[var(--bg-tertiary)] border border-[var(--border)] text-[13px] outline-none focus:border-[var(--accent)] transition-colors"
                  placeholder="you@company.com"
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <label htmlFor="password" className="text-[11px] uppercase tracking-wide text-[var(--fg-muted)] font-medium">
                  Password
                </label>
                <Link
                  href={`/forgot-password${nextQuery}`}
                  className="text-[11.5px] text-[var(--accent)] hover:text-[var(--accent-hover)] transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)]" />
                <input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-md bg-[var(--bg-tertiary)] border border-[var(--border)] text-[13px] outline-none focus:border-[var(--accent)] transition-colors"
                  placeholder="Your password"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-[var(--red)]/10 border border-[var(--red)]/30 px-3 py-2 text-[12px] text-[var(--red)]" role="alert">
                {error}
              </div>
            )}

            <Turnstile onTokenChange={setCaptchaToken} resetKey={captchaResetKey} />

            <button
              type="submit"
              disabled={loading || !email.trim() || !password || (turnstileEnabled && !captchaToken)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-50 text-white text-[13px] font-medium transition-colors focus-ring"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={13} />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-4 pt-4 border-t border-[var(--border)] text-center">
            {isSlackConnection ? (
              <p className="text-[11.5px] leading-relaxed text-[var(--fg-muted)]">
                Need Pauta access? Finish the workspace invite or account setup
                you received, then open the Slack connection link again.
              </p>
            ) : (
              <Link
                href={`/signup${nextQuery}`}
                className="text-[12px] text-[var(--fg-muted)] hover:text-[var(--fg-secondary)] transition-colors"
              >
                Don&apos;t have an account? Create one
              </Link>
            )}
          </div>
        </div>

        <p className="text-center text-[10.5px] text-[var(--fg-muted)] mt-4 leading-relaxed">
          Pauta never emails sign-in codes. Team invites use one single-use setup link; public signup confirmations and password resets are sent only when requested.
        </p>
      </motion.div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginInner />
    </Suspense>
  );
}
